import React, { useCallback, useMemo } from 'react';
import bem from 'utils/bem';
import { Short2RowsTeaser } from 'components/SpecialistTeaser';
import OrderDetails, { OrderDetailsSkeleton } from 'components/OrderDetails';
import Details from 'components/Details';
import { useMobile } from 'utils/media';
import { Short2RowsTeaserSkeleton } from 'components/SpecialistTeaser/Short2RowsTeaser';
import Timer, { useTimer } from 'components/OrderTeaser/Timer';
import CancelButton from 'components/OrderTeaser/CancelButton';
import PayButton from 'components/OrderTeaser/PayButton';
import RepeatButton from 'components/OrderTeaser/RepeatButton';
import TransferEventChip from 'components/OrderTeaser/TransferEventChip';
import Model from '../../../model';
import './index.sass';
import ContextEventChip from 'components/OrderTeaser/ContextEventChip';
import { Info } from 'components/Info';
import { StartMeetingButton } from 'components/OrderTeaser/StartMeetingButton';
import { TextWithPulsingDot } from 'components/TextWithPulsingDot';
import { GreenText } from 'components/GreenText';
import Text from 'components/Text';

const { block, el } = bem('order-teaser');

const EventChip = ({ meetingType, specId, openTransferForm }) => ({ event, number }) => {
    const openTransferFormForEvent = useCallback(() => {
        openTransferForm({ specId, orderId: event.orderId, meetingType, utcDate: event.startDate });
    }, []);
    return <ContextEventChip event={ event } number={ number } openTransferForm={ openTransferFormForEvent } />;
};

const OrderTeaser = ({ order }) => {
    const { isMobile } = useMobile();
    const { openTransferForm } = Model.modal;
    const specialist = order.data.specialist;
    const events = order.events;
    const address = order.data.event?.address ? order.data.event?.address : null;
    const meetingType = order.data.service.meetingType;
    const workingWithPairs = !!order.data?.pairVisit;
    const workingOffline = meetingType === 'OFF';
    const city = workingOffline ? order.data.event.address?.town : null;
    const district = workingOffline ? order.data.event.address?.district : null;
    const skype = !workingOffline && order.isOrderPaid ? order.data.specialist.skype : null;
    const ContextEventChip = EventChip({ meetingType, specId: specialist.id, openTransferForm });
    const showMenu = events.some((event) => event.isTransferEnabled || event.canStartMeeting);
    const OrderDetailsWidget =
        <OrderDetails
            workingOffline={ workingOffline }
            workingOfflineText={ workingOffline ?
                <span style={{fontWeight: 'bold'}}>Очная встреча</span> :
                <TextWithPulsingDot hasBackground={true} idle={true} >
                    <GreenText color={'#1C5E47'} fontWeight={'500'}>Онлайн-встреча</GreenText>
                </TextWithPulsingDot> }
            workingWithPairs={ workingWithPairs }
            city={ city }
            district={ district }
            skype={ skype }
            events={ events }
            address={ address }
            EventChip={ ContextEventChip }
        />;
    return (
        <div className={ el('wrapper') }>
            <div className={ block({ 'past': order.isOrderHistory }) }>
                <div className={ el('header') }>
                    <Short2RowsTeaser specialist={ { data: specialist } } />
                    { !isMobile && <div className={ el('buttons') }>
                        <Buttons order={ order } />
                    </div> }
                </div>
                <div className={ el('separator') }></div>
                { !workingOffline && <Info type='notification'>
                    <Text tag={'div'} style={{fontSize: '14px', fontWeight: 400}}>У вас <GreenText>онлайн</GreenText> встреча</Text>
                    <Text tag={'div'}>Пожалуйста, готовьте к онлайн встрече за 10-15 минут до начала</Text>
                </Info> }
                <div className={ el('details-block') }>
                    { OrderDetailsWidget }
                </div>
                <div className={ el('bottom') }>
                    { showMenu && <Info>Для того, чтобы перенести встречу или начать онлайн-консультацию, нажмите на троеточие в конкретной дате</Info> }
                    { typeof(order.closestOrderWithMeeting) !== 'undefined' && <StartMeetingButton orderId={order.closestOrderWithMeeting.id} /> }
                </div>
            </div>
            { isMobile && <div className={ el('buttons') }><Buttons order={ order } /></div> }
        </div>
    );
};

export const OrderTeaserSkeleton = () => {
    const { isMobile } = useMobile();
    const OrderDetailsWidget = isMobile ?
        <Details>
            <OrderDetailsSkeleton/>
        </Details> :
        <div className={ el('details-block') }>
            <OrderDetailsSkeleton/>
        </div>;
    return (
        <div className={ block() }>
            <div className={ el('header') }>
                <Short2RowsTeaserSkeleton/>
                <div className={ el('buttons') }>
                </div>
            </div>
            <div className={ el('separator') }></div>
            { OrderDetailsWidget }
        </div>
    );
};

const Buttons = ({ order }) => {
    if (!order.isOrderPaid && order.isOrderActive) {
        return <PayBlock order={ order }/>;
    }
    if (order.isOrderPaid && !order.isTransferAndCancelDisabled() && !order.isEventPassed()) {
        return <CancelBlock order={ order }/>;
    }
    if (order.isOrderHistory && order.isOrderPaid) {
        return <RepeatBlock order={ order }/>;
    }
};

const PayBlock = ({ order }) => {
    const { removeOrder } = Model.user;
    const onExpire = useCallback(() => {
        removeOrder(order.id)
    }, [ order.id ])
    return (
        <div className={ el('pay-block', { 'timer': !!order.payUntil }) }>
            { !!order.payUntil &&
                <>
                    <Timer expireTime={ order.payUntil } onExpire={ onExpire }/>
                    <div className={ el('pay-block-separator') }/>
                </>
            }
            <div className={ el('pay-block-buttons') }>
                <div className={ el('cancel-button') }>
                    <CancelButton order={ order }/>
                </div>
                <div className={ el('pay-button') }>
                    <PayButton order={ order }/>
                </div>
            </div>
        </div>
    );
};

const CancelBlock = ({ order }) => {
    return (
        <div className={ el('transfer-block') }>
            <div className={ el('cancel-button') }>
                <CancelButton order={ order }/>
            </div>
        </div>
    );
};

const RepeatBlock = ({ order }) => {
    const specialistId = order.data.specialist.id;
    const { getSpecialistById } = Model.specialist;
    const specialist = getSpecialistById(specialistId);
    if (!specialist) {
        return null;
    }
    const hasEvents = specialist.onlineCount > 0 || specialist.offlineCount > 0;
    if (!hasEvents) {
        return null;
    }
    return (
        <div className={ el('repeat-block') }>
            <div className={ el('repeat-button') }>
                <RepeatButton order={ order }/>
            </div>
        </div>
    );
};

export default OrderTeaser;
